import type {
  Archetype,
  AutonomyLevel,
  ActivityStatus,
  AuthorityVerdict,
  ClaimDenialReason,
  ClaimStatus,
  CoverageState,
  Audience,
  Impact,
  ImpactKind,
  IssueState,
  LossType,
  Pillar,
  SignalFamily,
  Tier,
} from "./types";

export const TIER_LABEL: Record<Tier, string> = {
  T0: "Unrated",
  T1: "Provisional",
  T2: "Standard",
  T3: "Preferred",
  T4: "Elite",
};

export function tierForScore(ars: number, credible = true): Tier {
  if (!credible) return "T0";
  if (ars >= 850) return "T4";
  if (ars >= 700) return "T3";
  if (ars >= 550) return "T2";
  return "T1";
}

export const PILLAR_LABEL: Record<Pillar, string> = {
  reliability: "Reliability",
  safety: "Safety & Conduct",
  security: "Security",
  controls: "Controls & Hygiene",
  remediation: "Remediation",
};

export const PILLAR_WEIGHT: Record<Pillar, number> = {
  reliability: 0.25,
  safety: 0.25,
  security: 0.2,
  controls: 0.15,
  remediation: 0.15,
};

export const ISSUE_STATE_LABEL: Record<IssueState, string> = {
  detected: "Detected",
  grace: "Grace",
  mitigated: "Mitigated",
  verifying: "Verifying",
  resolved: "Resolved",
  excluded: "Excluded",
  regressed: "Regressed",
};

export const COVERAGE_STATE_LABEL: Record<CoverageState, string> = {
  covered: "Covered",
  grace: "In grace",
  mitigated: "Mitigated",
  verifying: "Verifying",
  graduated: "Graduated",
  excluded: "Excluded",
  unobserved: "Unobserved",
};

export const ARCHETYPE_LABEL: Record<Archetype, string> = {
  customer_support: "Customer support",
  commerce: "Commerce",
  fintech_ops: "Fintech ops",
  sales: "Sales",
  coding: "Coding",
  internal_ops: "Internal ops",
  research: "Research",
  other: "Other",
};

export const AUTONOMY_LABEL: Record<AutonomyLevel, string> = {
  L0: "L0 · Suggest only",
  L1: "L1 · Act with approval",
  L2: "L2 · Act within caps",
  L3: "L3 · Broad, guarded",
  L4: "L4 · Fully autonomous",
};


export const FAMILY_LABEL: Record<SignalFamily, string> = {
  action_safety: "Action safety",
  security: "Security",
  data_protection: "Data protection",
  output_integrity: "Output integrity",
  control_flow: "Control flow",
  tool_use: "Tool use",
  integrity: "Integrity",
  outcomes: "Outcomes & UX",
  economics: "Economics",
  reliability: "Reliability & fleet",
};

export const AUDIENCE_LABEL: Record<Audience, string> = {
  external_user: "External users",
  internal_user: "Internal users",
  machine: "Other agents / systems",
};

export const ACTIVITY_STATUS_LABEL: Record<ActivityStatus, string> = {
  completed: "Completed",
  pending: "Pending",
  failed: "Failed",
  blocked: "Blocked",
};

export const AUTHORITY_LABEL: Record<AuthorityVerdict, string> = {
  within: "Within authority",
  outside: "Outside authority",
  unknown: "Unknown",
};

export const IMPACT_KIND_LABEL: Record<ImpactKind, string> = {
  monetary: "Money",
  records: "Records",
  messages: "Messages",
  resources: "Resources",
  compute: "Compute",
  files: "Files",
  custom: "Other",
};

export const CLAIM_DENIAL_LABEL: Record<ClaimDenialReason, string> = {
  not_covered: "Loss not covered by the policy",
  excluded: "Excluded scenario or loss",
  outside_period: "Incident outside the policy period",
  below_deductible: "Loss within the deductible",
  limits_exhausted: "Limits exhausted",
  provoked: "Loss induced by the claimant",
  insufficient_evidence: "Insufficient evidence of loss",
  late_notice: "Notice given too late",
  duplicate: "Duplicate of another claim",
  misrepresentation: "Material misrepresentation",
};

export const LOSS_TYPE_LABEL: Record<LossType, string> = {
  first_party: "First-party (own loss)",
  third_party: "Third-party (people affected)",
};

/** Human-readable impact: "$420.00", "4,212 rows", "37 emails", "3 pods". */
export function formatImpact(i: Impact | null | undefined, opts: { compact?: boolean } = {}): string {
  if (!i) return "—";
  if (i.kind === "monetary") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: i.unit,
      notation: opts.compact && Math.abs(i.amount) >= 10_000 ? "compact" : "standard",
      maximumFractionDigits: Math.abs(i.amount) < 100 && i.amount % 1 !== 0 ? 2 : 0,
    }).format(i.amount);
  }
  const n = new Intl.NumberFormat("en-US", { notation: opts.compact && i.amount >= 10_000 ? "compact" : "standard", maximumFractionDigits: 1 }).format(i.amount);
  return `${n} ${i.unit}`;
}

/** Sum impacts per kind+unit. */
export function sumImpacts(list: Array<Impact | null | undefined>): Impact[] {
  const by = new Map<string, Impact>();
  for (const i of list) {
    if (!i) continue;
    const k = `${i.kind}:${i.unit}`;
    const cur = by.get(k);
    by.set(k, cur ? { ...cur, amount: cur.amount + i.amount } : { ...i });
  }
  return [...by.values()].sort((a, b) => (a.kind === "monetary" ? -1 : b.kind === "monetary" ? 1 : b.amount - a.amount));
}

export const CLAIM_STATUS_LABEL: Record<ClaimStatus, string> = {
  prompted: "Prompted",
  filed: "Filed",
  evidence_ready: "Evidence ready",
  under_review: "Under review",
  approved: "Approved",
  paid: "Paid",
  denied: "Denied",
};

/** Grace windows by severity (decision D2). */
export const GRACE_WINDOW_HOURS = { critical: 48, high: 168, medium: 336, low: 720 } as const;
